import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiConfiguration } from '../api-configuration';
import { BaseService } from '../base-service';
import {
  AcademySubscriptionState,
  AcademySubscriptionSummary,
  AcademyFeatureFlag,
  PlatformAcademy,
  PlatformAcademyList,
  OtpBranchRow,
  OtpSubscriberRow,
  OtpUsageSummary,
  PlatformAcademyPagination,
  PlatformUser,
  PlatformUserWrite,
  FranchisePermissionMatrix,
  FinanceReportPayload,
  UnregisteredPhoneAttempt,
  AuditLogRow,
  AuditLookup,
  PlatformCatalogList,
  PlatformCatalogRow,
  PlatformAcademyWrite,
  AcademyFeaturesBoard,
  PlatformCountry,
  PlatformLocationOption,
  PlatformEligibleManager,
  SubscriptionBoardItem,
  ComprehensiveAcademyReportResponse,
  ComprehensiveAcademyReportRow,
  CommissionRuleItem,
  CommissionSettingsData,
  PaymentLinkSettingsData,
  SettlementResult,
  PlatformCustomMessageItem,
  PlatformCustomMessageList,
} from '../models/super-admin-manual.models';

interface ApiEnvelope<T> {
  status: 'success' | 'error';
  data: T;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class SuperAdminManualService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  listAcademies(
    page: number,
    perPage: number,
    search: string,
    status: number | null = null,
    countryId: number | null = null,
    managerId: number | null = null,
    showInPartners: number | null = null,
    subscription: AcademySubscriptionState | null = null,
  ): Observable<ApiEnvelope<PlatformAcademyList>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('per_page', String(perPage));
    if (subscription) {
      params = params.set('subscription', subscription);
    }

    const term = search.trim();
    if (term) {
      params = params.set('search', term);
    }
    if (status === 0 || status === 1) {
      params = params.set('status', String(status));
    }
    if (countryId) {
      params = params.set('country_id', String(countryId));
    }
    if (managerId) {
      params = params.set('manager_id', String(managerId));
    }
    if (showInPartners === 0 || showInPartners === 1) {
      params = params.set('show_in_partners', String(showInPartners));
    }

    return this.http.get<ApiEnvelope<PlatformAcademyList>>(
      `${this.rootUrl}/api/v1/super-admin/academies`,
      { params }
    );
  }

  exportAcademies(
    search = '',
    status: number | null = null,
    countryId: number | null = null,
    managerId: number | null = null,
    showInPartners: number | null = null,
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status === 0 || status === 1) {
      params = params.set('status', String(status));
    }
    if (countryId) {
      params = params.set('country_id', String(countryId));
    }
    if (managerId) {
      params = params.set('manager_id', String(managerId));
    }
    if (showInPartners === 0 || showInPartners === 1) {
      params = params.set('show_in_partners', String(showInPartners));
    }
    return this.http.get(`${this.rootUrl}/api/v1/super-admin/academies/export`, {
      params,
      observe: 'response',
      responseType: 'blob',
    });
  }

  toggleAcademyStatus(id: number): Observable<ApiEnvelope<{ academy: PlatformAcademy }>> {
    return this.http.patch<ApiEnvelope<{ academy: PlatformAcademy }>>(
      `${this.rootUrl}/api/v1/super-admin/academies/${id}/status`,
      {}
    );
  }

  toggleAcademyFlag(id: number, flag: AcademyFeatureFlag): Observable<ApiEnvelope<{ academy: PlatformAcademy }>> {
    return this.http.patch<ApiEnvelope<{ academy: PlatformAcademy }>>(
      `${this.rootUrl}/api/v1/super-admin/academies/${id}/${flag}`,
      {}
    );
  }

  listCountries(): Observable<ApiEnvelope<{ items: PlatformCountry[] }>> {
    return this.http.get<ApiEnvelope<{ items: PlatformCountry[] }>>(
      `${this.rootUrl}/api/v1/super-admin/countries`
    );
  }

  listAcademyCities(countryId: number): Observable<ApiEnvelope<{ items: PlatformLocationOption[] }>> {
    return this.http.get<ApiEnvelope<{ items: PlatformLocationOption[] }>>(
      `${this.rootUrl}/api/v1/super-admin/countries/${countryId}/cities`
    );
  }

  listAcademyDistricts(cityId: number): Observable<ApiEnvelope<{ items: PlatformLocationOption[] }>> {
    return this.http.get<ApiEnvelope<{ items: PlatformLocationOption[] }>>(
      `${this.rootUrl}/api/v1/super-admin/cities/${cityId}/districts`
    );
  }

  listEligibleAcademyManagers(
    academyId: number | null = null,
    parentId: number | null = null,
    search = '',
  ): Observable<ApiEnvelope<{ items: PlatformEligibleManager[] }>> {
    let params = new HttpParams();
    if (academyId) {
      params = params.set('academy_id', String(academyId));
    }
    if (parentId) {
      params = params.set('parent_id', String(parentId));
    }
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    return this.http.get<ApiEnvelope<{ items: PlatformEligibleManager[] }>>(
      `${this.rootUrl}/api/v1/super-admin/academies/eligible-managers`,
      { params }
    );
  }

  createAcademy(body: PlatformAcademyWrite): Observable<ApiEnvelope<{ academy: PlatformAcademy }>> {
    return this.http.post<ApiEnvelope<{ academy: PlatformAcademy }>>(
      `${this.rootUrl}/api/v1/super-admin/academies`,
      body
    );
  }

  updateAcademy(id: number, body: PlatformAcademyWrite): Observable<ApiEnvelope<{ academy: PlatformAcademy }>> {
    return this.http.put<ApiEnvelope<{ academy: PlatformAcademy }>>(
      `${this.rootUrl}/api/v1/super-admin/academies/${id}`,
      body
    );
  }

  getAcademy(id: number | string): Observable<ApiEnvelope<{ academy: PlatformAcademy }>> {
    return this.http.get<ApiEnvelope<{ academy: PlatformAcademy }>>(
      `${this.rootUrl}/api/v1/super-admin/academies/${id}`
    );
  }

  listBranches(id: number, page: number, perPage = 15): Observable<ApiEnvelope<PlatformAcademyList>> {
    const params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    return this.http.get<ApiEnvelope<PlatformAcademyList>>(
      `${this.rootUrl}/api/v1/super-admin/academies/${id}/branches`,
      { params }
    );
  }

  createBranch(id: number, body: PlatformAcademyWrite): Observable<ApiEnvelope<{ academy: PlatformAcademy }>> {
    return this.http.post<ApiEnvelope<{ academy: PlatformAcademy }>>(
      `${this.rootUrl}/api/v1/super-admin/academies/${id}/branches`,
      body
    );
  }

  otpReport(page: number, perPage = 20): Observable<ApiEnvelope<{ summary: OtpUsageSummary; items: OtpBranchRow[]; pagination: PlatformAcademyPagination }>> {
    const params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    return this.http.get<ApiEnvelope<{ summary: OtpUsageSummary; items: OtpBranchRow[]; pagination: PlatformAcademyPagination }>>(
      `${this.rootUrl}/api/v1/super-admin/otp-report`,
      { params }
    );
  }

  otpAcademy(id: number, page: number, perPage = 20): Observable<ApiEnvelope<{ academy: { id: number; title: string }; summary: OtpUsageSummary; items: OtpSubscriberRow[]; pagination: PlatformAcademyPagination }>> {
    const params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    return this.http.get<ApiEnvelope<{ academy: { id: number; title: string }; summary: OtpUsageSummary; items: OtpSubscriberRow[]; pagination: PlatformAcademyPagination }>>(
      `${this.rootUrl}/api/v1/super-admin/otp-report/${id}`,
      { params }
    );
  }

  listPhoneAttempts(
    page: number,
    search = '',
    attempts: number | null = null,
    perPage = 20,
  ): Observable<ApiEnvelope<{ items: UnregisteredPhoneAttempt[]; pagination: PlatformAcademyPagination }>> {
    let params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    if (attempts !== null) {
      params = params.set('attempts', String(attempts));
    }
    return this.http.get<ApiEnvelope<{ items: UnregisteredPhoneAttempt[]; pagination: PlatformAcademyPagination }>>(
      `${this.rootUrl}/api/v1/super-admin/unregistered-phones`,
      { params }
    );
  }

  createPhoneAttempt(body: { mobile: string; attempts: number | null }): Observable<ApiEnvelope<{ attempt: UnregisteredPhoneAttempt }>> {
    return this.http.post<ApiEnvelope<{ attempt: UnregisteredPhoneAttempt }>>(
      `${this.rootUrl}/api/v1/super-admin/unregistered-phones`,
      body
    );
  }

  updatePhoneAttempt(id: number, body: { mobile: string; attempts: number | null }): Observable<ApiEnvelope<{ attempt: UnregisteredPhoneAttempt }>> {
    return this.http.put<ApiEnvelope<{ attempt: UnregisteredPhoneAttempt }>>(
      `${this.rootUrl}/api/v1/super-admin/unregistered-phones/${id}`,
      body
    );
  }

  exportPhoneAttempts(search = '', attempts: number | null = null): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    if (attempts !== null) {
      params = params.set('attempts', String(attempts));
    }
    return this.http.get(`${this.rootUrl}/api/v1/super-admin/unregistered-phones/export`, {
      params,
      observe: 'response',
      responseType: 'blob',
    });
  }

  listUsers(
    kind: string,
    page: number,
    search = '',
    status: number | null = null,
    academyId: number | null = null,
    perPage = 15,
  ): Observable<ApiEnvelope<{ items: PlatformUser[]; pagination: PlatformAcademyPagination }>> {
    let params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status !== null) {
      params = params.set('status', String(status));
    }
    if (academyId !== null) {
      params = params.set('academy_id', String(academyId));
    }
    return this.http.get<ApiEnvelope<{ items: PlatformUser[]; pagination: PlatformAcademyPagination }>>(
      `${this.rootUrl}/api/v1/super-admin/users/${kind}`,
      { params }
    );
  }

  exportUsers(kind: string, search = '', status: number | null = null, academyId: number | null = null): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status !== null) {
      params = params.set('status', String(status));
    }
    if (academyId !== null) {
      params = params.set('academy_id', String(academyId));
    }
    return this.http.get(`${this.rootUrl}/api/v1/super-admin/users/${kind}/export`, {
      params,
      observe: 'response',
      responseType: 'blob',
    });
  }

  createUser(kind: string, body: PlatformUserWrite): Observable<ApiEnvelope<{ user: PlatformUser }>> {
    return this.http.post<ApiEnvelope<{ user: PlatformUser }>>(`${this.rootUrl}/api/v1/super-admin/users/${kind}`, body);
  }

  updateUser(kind: string, id: number, body: PlatformUserWrite): Observable<ApiEnvelope<{ user: PlatformUser }>> {
    return this.http.put<ApiEnvelope<{ user: PlatformUser }>>(`${this.rootUrl}/api/v1/super-admin/users/${kind}/${id}`, body);
  }

  toggleUserStatus(kind: string, id: number): Observable<ApiEnvelope<{ user: PlatformUser }>> {
    return this.http.patch<ApiEnvelope<{ user: PlatformUser }>>(`${this.rootUrl}/api/v1/super-admin/users/${kind}/${id}/status`, {});
  }

  franchisePermissions(id: number): Observable<ApiEnvelope<FranchisePermissionMatrix>> {
    return this.http.get<ApiEnvelope<FranchisePermissionMatrix>>(
      `${this.rootUrl}/api/v1/super-admin/users/franchise-managers/${id}/permissions`
    );
  }

  saveFranchisePermissions(id: number, grants: Record<string, string[]>): Observable<ApiEnvelope<FranchisePermissionMatrix>> {
    return this.http.put<ApiEnvelope<FranchisePermissionMatrix>>(
      `${this.rootUrl}/api/v1/super-admin/users/franchise-managers/${id}/permissions`,
      { grants }
    );
  }

  resetFranchisePermissions(id: number, academyIds: number[]): Observable<ApiEnvelope<FranchisePermissionMatrix>> {
    return this.http.post<ApiEnvelope<FranchisePermissionMatrix>>(
      `${this.rootUrl}/api/v1/super-admin/users/franchise-managers/${id}/permissions/reset`,
      { academy_ids: academyIds }
    );
  }

  financeReport(
    kind: 'payment-links' | 'commissions',
    page: number,
    filters: { academyId?: number | null; dateFrom?: string; dateTo?: string; settlementStatus?: string } = {},
  ): Observable<ApiEnvelope<FinanceReportPayload>> {
    let params = new HttpParams().set('page', String(page)).set('per_page', '15');
    if (filters.academyId) {
      params = params.set('academy_id', String(filters.academyId));
    }
    if (filters.dateFrom) {
      params = params.set('date_from', filters.dateFrom);
    }
    if (filters.dateTo) {
      params = params.set('date_to', filters.dateTo);
    }
    if (filters.settlementStatus) {
      params = params.set('settlement_status', filters.settlementStatus);
    }
    return this.http.get<ApiEnvelope<FinanceReportPayload>>(
      `${this.rootUrl}/api/v1/super-admin/reports/${kind}`,
      { params }
    );
  }

  deletePhoneAttempt(id: number): Observable<ApiEnvelope<{ deleted: boolean }>> {
    return this.http.delete<ApiEnvelope<{ deleted: boolean }>>(
      `${this.rootUrl}/api/v1/super-admin/unregistered-phones/${id}`
    );
  }

  listSubscriptionBoard(
    section: string,
    page: number,
    perPage: number,
    query: Record<string, string | number | null>,
  ): Observable<ApiEnvelope<{ items: SubscriptionBoardItem[]; pagination: PlatformAcademyPagination }>> {
    return this.http.get<ApiEnvelope<{ items: SubscriptionBoardItem[]; pagination: PlatformAcademyPagination }>>(
      `${this.rootUrl}/api/v1/super-admin/subscriptions/${section}`,
      { params: this.boardParams(page, perPage, query) }
    );
  }

  showSubscriptionBoard(section: string, id: number): Observable<ApiEnvelope<{ item: SubscriptionBoardItem }>> {
    return this.http.get<ApiEnvelope<{ item: SubscriptionBoardItem }>>(
      `${this.rootUrl}/api/v1/super-admin/subscriptions/${section}/${id}`
    );
  }

  saveSubscriptionBoard(
    section: string,
    body: Record<string, unknown>,
    id?: number,
  ): Observable<ApiEnvelope<{ item?: SubscriptionBoardItem; result?: unknown }>> {
    const url = `${this.rootUrl}/api/v1/super-admin/subscriptions/${section}${id ? '/' + id : ''}`;
    return id
      ? this.http.put<ApiEnvelope<{ item?: SubscriptionBoardItem }>>(url, body)
      : this.http.post<ApiEnvelope<{ item?: SubscriptionBoardItem; result?: unknown }>>(url, body);
  }

  subscriptionStatus(
    section: 'plans' | 'customers' | 'promo-codes',
    id: number,
    body: Record<string, unknown>,
  ): Observable<ApiEnvelope<{ item: SubscriptionBoardItem }>> {
    return this.http.post<ApiEnvelope<{ item: SubscriptionBoardItem }>>(
      `${this.rootUrl}/api/v1/super-admin/subscriptions/${section}/${id}/status`,
      body
    );
  }

  subscriptionInvoices(id: number): Observable<ApiEnvelope<{ result: Array<{ invoice_number?: string; amount?: string | number; currency?: string; status?: { label?: string } }> }>> {
    return this.http.get<ApiEnvelope<{ result: Array<{ invoice_number?: string; amount?: string | number; currency?: string; status?: { label?: string } }> }>>(
      `${this.rootUrl}/api/v1/super-admin/subscriptions/list/${id}/invoices`
    );
  }

  deleteAcademyNote(id: number): Observable<ApiEnvelope<{ deleted: boolean }>> {
    return this.http.delete<ApiEnvelope<{ deleted: boolean }>>(
      `${this.rootUrl}/api/v1/super-admin/subscriptions/academy-notes/${id}`
    );
  }

  listAuditLogs(
    page: number,
    perPage: number,
    query: Record<string, string | number | null>,
  ): Observable<ApiEnvelope<{ items: AuditLogRow[]; pagination: PlatformAcademyPagination }>> {
    return this.http.get<ApiEnvelope<{ items: AuditLogRow[]; pagination: PlatformAcademyPagination }>>(
      `${this.rootUrl}/api/v1/super-admin/audit-logs`,
      { params: this.boardParams(page, perPage, query) }
    );
  }

  showAuditLog(id: number): Observable<ApiEnvelope<{ item: AuditLogRow }>> {
    return this.http.get<ApiEnvelope<{ item: AuditLogRow }>>(`${this.rootUrl}/api/v1/super-admin/audit-logs/${id}`);
  }

  exportAuditLogs(query: Record<string, string | number | null>): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.rootUrl}/api/v1/super-admin/audit-logs/export`, {
      params: this.boardParams(1, 20, query),
      observe: 'response',
      responseType: 'blob',
    });
  }

  auditUsers(search = ''): Observable<ApiEnvelope<{ items: AuditLookup[] }>> {
    let params = new HttpParams();
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    return this.http.get<ApiEnvelope<{ items: AuditLookup[] }>>(`${this.rootUrl}/api/v1/super-admin/audit-logs/users`, { params });
  }

  auditAcademies(search = ''): Observable<ApiEnvelope<{ items: AuditLookup[] }>> {
    let params = new HttpParams();
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    return this.http.get<ApiEnvelope<{ items: AuditLookup[] }>>(`${this.rootUrl}/api/v1/super-admin/audit-logs/academies`, { params });
  }

  listCustomMessages(
    page: number,
    perPage: number,
    query: Record<string, string | number | null>,
  ): Observable<ApiEnvelope<PlatformCustomMessageList>> {
    return this.http.get<ApiEnvelope<PlatformCustomMessageList>>(
      `${this.rootUrl}/api/v1/super-admin/custom-messages`,
      { params: this.boardParams(page, perPage, query) }
    );
  }

  showCustomMessage(id: number): Observable<ApiEnvelope<{ item: PlatformCustomMessageItem }>> {
    return this.http.get<ApiEnvelope<{ item: PlatformCustomMessageItem }>>(
      `${this.rootUrl}/api/v1/super-admin/custom-messages/${id}`
    );
  }

  approveCustomMessage(id: number): Observable<ApiEnvelope<{ item: PlatformCustomMessageItem; message: string }>> {
    return this.http.post<ApiEnvelope<{ item: PlatformCustomMessageItem; message: string }>>(
      `${this.rootUrl}/api/v1/super-admin/custom-messages/${id}/approve`,
      {}
    );
  }

  rejectCustomMessage(id: number, reason?: string | null): Observable<ApiEnvelope<{ item: PlatformCustomMessageItem; message: string }>> {
    return this.http.post<ApiEnvelope<{ item: PlatformCustomMessageItem; message: string }>>(
      `${this.rootUrl}/api/v1/super-admin/custom-messages/${id}/reject`,
      { reason: reason?.trim() || null }
    );
  }

  listCatalog(
    area: string,
    section: string,
    page: number,
    perPage: number,
    query: Record<string, string | number | null>,
  ): Observable<ApiEnvelope<PlatformCatalogList>> {
    return this.http.get<ApiEnvelope<PlatformCatalogList>>(
      `${this.rootUrl}/api/v1/super-admin/platform/${area}/${section}`,
      { params: this.boardParams(page, perPage, query) }
    );
  }

  saveCatalog(
    area: string,
    section: string,
    payload: Record<string, string | number | null>,
    id?: number | null,
  ): Observable<ApiEnvelope<{ item: PlatformCatalogRow }>> {
    const url = id
      ? `${this.rootUrl}/api/v1/super-admin/platform/${area}/${section}/${id}`
      : `${this.rootUrl}/api/v1/super-admin/platform/${area}/${section}`;
    return id
      ? this.http.put<ApiEnvelope<{ item: PlatformCatalogRow }>>(url, payload)
      : this.http.post<ApiEnvelope<{ item: PlatformCatalogRow }>>(url, payload);
  }

  saveSiteSettings(payload: Record<string, string | number | null>): Observable<ApiEnvelope<{ item: PlatformCatalogRow }>> {
    return this.http.put<ApiEnvelope<{ item: PlatformCatalogRow }>>(
      `${this.rootUrl}/api/v1/super-admin/platform/content/settings`,
      payload
    );
  }

  setCatalogStatus(area: string, section: string, id: number, status: number): Observable<ApiEnvelope<{ item: PlatformCatalogRow }>> {
    return this.http.post<ApiEnvelope<{ item: PlatformCatalogRow }>>(
      `${this.rootUrl}/api/v1/super-admin/platform/${area}/${section}/${id}/status`,
      { status }
    );
  }

  deleteCatalog(area: string, section: string, id: number): Observable<ApiEnvelope<{ deleted: boolean }>> {
    return this.http.delete<ApiEnvelope<{ deleted: boolean }>>(
      `${this.rootUrl}/api/v1/super-admin/platform/${area}/${section}/${id}`
    );
  }

  private boardParams(page: number, perPage: number, query: Record<string, string | number | null>): HttpParams {
    let params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    Object.entries(query).forEach(([key, value]) => {
      if (value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  academySubscriptionSummary(): Observable<ApiEnvelope<{ subscription: AcademySubscriptionSummary }>> {
    return this.http.get<ApiEnvelope<{ subscription: AcademySubscriptionSummary }>>(
      `${this.rootUrl}/api/v1/super-admin/academies/summary`
    );
  }

  renewAcademy(
    id: number,
    durationValue: number,
    durationUnit: 'day' | 'month' | 'year',
  ): Observable<ApiEnvelope<{ academy: PlatformAcademy }>> {
    return this.http.post<ApiEnvelope<{ academy: PlatformAcademy }>>(
      `${this.rootUrl}/api/v1/super-admin/academies/${id}/renew`,
      { duration_value: durationValue, duration_unit: durationUnit }
    );
  }

  comprehensiveAcademyReport(
    page: number,
    perPage: number,
    filters: { search?: string; email?: string; status?: number | null; dateFrom?: string; dateTo?: string } = {},
  ): Observable<ApiEnvelope<ComprehensiveAcademyReportResponse>> {
    let params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    if (filters.search?.trim()) {
      params = params.set('search', filters.search.trim());
    }
    if (filters.email?.trim()) {
      params = params.set('email', filters.email.trim());
    }
    if (filters.status === 0 || filters.status === 1) {
      params = params.set('status', String(filters.status));
    }
    if (filters.dateFrom) {
      params = params.set('date_from', filters.dateFrom);
    }
    if (filters.dateTo) {
      params = params.set('date_to', filters.dateTo);
    }
    return this.http.get<ApiEnvelope<ComprehensiveAcademyReportResponse>>(
      `${this.rootUrl}/api/v1/super-admin/reports/academies`,
      { params }
    );
  }

  comprehensiveAcademyDetails(
    academyId: number,
    filters: { dateFrom?: string; dateTo?: string } = {},
  ): Observable<ApiEnvelope<ComprehensiveAcademyReportRow & { notes?: any[]; weekly_logs?: any[] }>> {
    let params = new HttpParams();
    if (filters.dateFrom) {
      params = params.set('date_from', filters.dateFrom);
    }
    if (filters.dateTo) {
      params = params.set('date_to', filters.dateTo);
    }
    return this.http.get<ApiEnvelope<ComprehensiveAcademyReportRow & { notes?: any[]; weekly_logs?: any[] }>>(
      `${this.rootUrl}/api/v1/super-admin/reports/academies/${academyId}`,
      { params }
    );
  }

  exportComprehensiveAcademyReport(
    filters: { search?: string; email?: string; status?: number | null; dateFrom?: string; dateTo?: string } = {},
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (filters.search?.trim()) {
      params = params.set('search', filters.search.trim());
    }
    if (filters.email?.trim()) {
      params = params.set('email', filters.email.trim());
    }
    if (filters.status === 0 || filters.status === 1) {
      params = params.set('status', String(filters.status));
    }
    if (filters.dateFrom) {
      params = params.set('date_from', filters.dateFrom);
    }
    if (filters.dateTo) {
      params = params.set('date_to', filters.dateTo);
    }
    return this.http.get(`${this.rootUrl}/api/v1/super-admin/reports/academies/export`, {
      params,
      observe: 'response',
      responseType: 'blob',
    });
  }

  // Commission Settings
  getCommissionSettings(): Observable<ApiEnvelope<CommissionSettingsData>> {
    return this.http.get<ApiEnvelope<CommissionSettingsData>>(
      `${this.rootUrl}/api/v1/super-admin/settings/commissions`
    );
  }

  saveGlobalCommission(body: { commission_type: string; commission_value: number; active: boolean }): Observable<ApiEnvelope<CommissionRuleItem>> {
    return this.http.post<ApiEnvelope<CommissionRuleItem>>(
      `${this.rootUrl}/api/v1/super-admin/settings/commissions/global`,
      body
    );
  }

  saveAcademyCommission(body: { id?: number; academy_id: number; commission_type: string; commission_value: number; active: boolean }): Observable<ApiEnvelope<CommissionRuleItem>> {
    return this.http.post<ApiEnvelope<CommissionRuleItem>>(
      `${this.rootUrl}/api/v1/super-admin/settings/commissions/academy`,
      body
    );
  }

  saveCustomerCommission(body: { id?: number; customer_id: number; academy_id?: number | null; commission_type: string; commission_value: number; active: boolean }): Observable<ApiEnvelope<CommissionRuleItem>> {
    return this.http.post<ApiEnvelope<CommissionRuleItem>>(
      `${this.rootUrl}/api/v1/super-admin/settings/commissions/customer`,
      body
    );
  }

  toggleCommissionRule(id: number): Observable<ApiEnvelope<CommissionRuleItem>> {
    return this.http.patch<ApiEnvelope<CommissionRuleItem>>(
      `${this.rootUrl}/api/v1/super-admin/settings/commissions/${id}/status`,
      {}
    );
  }

  deleteCommissionRule(id: number): Observable<ApiEnvelope<null>> {
    return this.http.delete<ApiEnvelope<null>>(
      `${this.rootUrl}/api/v1/super-admin/settings/commissions/${id}`
    );
  }

  // Payment Link Settings
  listAcademyFeatures(
    page = 1,
    perPage = 20,
    search = '',
    status?: number | null,
    subscription?: 'active' | 'expired' | null,
  ): Observable<ApiEnvelope<AcademyFeaturesBoard>> {
    let params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status === 0 || status === 1) {
      params = params.set('status', String(status));
    }
    if (subscription === 'active' || subscription === 'expired') {
      params = params.set('subscription', subscription);
    }
    return this.http.get<ApiEnvelope<AcademyFeaturesBoard>>(
      `${this.rootUrl}/api/v1/super-admin/academy-features`,
      { params },
    );
  }

  updateAcademyFeature(
    academyId: number,
    featureKey: string,
    isEnabled: boolean,
  ): Observable<ApiEnvelope<{ academy_id: number; feature_key: string; is_enabled: boolean }>> {
    return this.http.post<ApiEnvelope<{ academy_id: number; feature_key: string; is_enabled: boolean }>>(
      `${this.rootUrl}/api/v1/super-admin/academy-features/${academyId}`,
      { feature_key: featureKey, is_enabled: isEnabled },
    );
  }

  getPaymentLinkSettings(page = 1, perPage = 20, search = '', status?: number | null): Observable<ApiEnvelope<PaymentLinkSettingsData>> {
    let params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status !== null && status !== undefined) {
      params = params.set('status', String(status));
    }
    return this.http.get<ApiEnvelope<PaymentLinkSettingsData>>(
      `${this.rootUrl}/api/v1/super-admin/settings/payment-links`,
      { params }
    );
  }

  togglePaymentLink(academyId: number, enabled: boolean): Observable<ApiEnvelope<{ academy_id: number; payment_link_enabled: boolean }>> {
    return this.http.post<ApiEnvelope<{ academy_id: number; payment_link_enabled: boolean }>>(
      `${this.rootUrl}/api/v1/super-admin/settings/payment-links/${academyId}/toggle`,
      { enabled }
    );
  }

  bulkUpdatePaymentLinks(enabled: boolean, academyIds?: number[]): Observable<ApiEnvelope<{ updated_count: number; enabled: boolean }>> {
    return this.http.post<ApiEnvelope<{ updated_count: number; enabled: boolean }>>(
      `${this.rootUrl}/api/v1/super-admin/settings/payment-links/bulk`,
      { enabled, academy_ids: academyIds }
    );
  }

  // Settlement Action
  settleCommissionTransaction(id: number, transferReference?: string): Observable<ApiEnvelope<SettlementResult>> {
    return this.http.post<ApiEnvelope<SettlementResult>>(
      `${this.rootUrl}/api/v1/super-admin/reports/commissions/${id}/settle`,
      { transfer_reference: transferReference }
    );
  }
}
